import { useEffect, useState } from "react";
import api from "@/api";
import { toast } from "sonner";

export default function AdminInventory() {
  const [rows, setRows] = useState(null);
  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState("stock");
  const [adjust, setAdjust] = useState(null);
  const [form, setForm] = useState({ change: "", reason: "restock" });
  const [filter, setFilter] = useState("all");

  const load = () => {
    api.get("/admin/inventory").then(({ data }) => setRows(data));
    api.get("/admin/inventory/logs").then(({ data }) => setLogs(data));
  };
  useEffect(() => { load(); }, []);

  const saveAdjust = async (e) => {
    e.preventDefault();
    const change = parseInt(form.change, 10);
    if (!change) { toast.error("Enter a non-zero change"); return; }
    try {
      await api.post("/admin/inventory/adjust", { product_id: adjust.product_id, variant_id: adjust.variant_id, change, reason: form.reason });
      toast.success("Stock updated");
      setAdjust(null);
      setForm({ change: "", reason: "restock" });
      load();
    } catch (err) { toast.error(err.friendlyMessage); }
  };

  const filtered = (rows || []).filter((r) => filter === "all" || r.status === filter);

  return (
    <div data-testid="admin-inventory">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-serif text-3xl">Inventory</h1>
        <div className="flex gap-2">
          {[["stock", "Stock Levels"], ["logs", "History"]].map(([id, l]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`inventory-tab-${id}`}
              className={`tq-btn !py-2 !px-4 ${tab === id ? "bg-[#121212] text-white" : "border border-[#E5DEC9]"}`}>{l}</button>
          ))}
        </div>
      </div>
      {tab === "stock" && (
        <>
          <div className="flex gap-2 mb-4">
            {[["all", "All"], ["low", "Low Stock"], ["out", "Out of Stock"]].map(([id, l]) => (
              <button key={id} onClick={() => setFilter(id)} data-testid={`inventory-filter-${id}`}
                className={`px-3 py-1.5 text-xs border ${filter === id ? "bg-[#121212] text-white border-[#121212]" : "border-[#E5DEC9] bg-white"}`}>{l}</button>
            ))}
          </div>
          {!rows ? <div className="h-64 bg-white animate-pulse" /> : (
            <div className="bg-white border border-[#E5DEC9] overflow-x-auto">
              <table className="w-full text-sm min-w-[680px]">
                <thead><tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#8C7A5E] border-b border-[#E5DEC9]">
                  <th className="p-3">SKU</th><th className="p-3">Product</th><th className="p-3">Size</th><th className="p-3">Color</th><th className="p-3">Stock</th><th className="p-3">Status</th><th className="p-3">Adjust</th>
                </tr></thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.variant_id} className="border-b border-[#F4EFE6]" data-testid={`inventory-row-${r.sku}`}>
                      <td className="p-3 font-mono text-xs">{r.sku}</td>
                      <td className="p-3">{r.product}</td>
                      <td className="p-3">{r.size}</td>
                      <td className="p-3">{r.color}</td>
                      <td className="p-3 font-semibold">{r.stock}</td>
                      <td className="p-3"><span className={`text-[10px] font-bold uppercase px-2 py-1 ${r.status === "out" ? "bg-[#8B1E21]/10 text-[#8B1E21]" : r.status === "low" ? "bg-[#D4AF37]/15 text-[#8C7A5E]" : "bg-[#1A3C34]/10 text-[#1A3C34]"}`}>{r.status === "out" ? "Out of stock" : r.status === "low" ? "Low" : "OK"}</span></td>
                      <td className="p-3"><button onClick={() => setAdjust(r)} data-testid={`inventory-adjust-${r.sku}`} className="tq-btn-outline !py-1.5 !px-3 !text-[10px]">Adjust</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {tab === "logs" && (
        <div className="bg-white border border-[#E5DEC9] overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#8C7A5E] border-b border-[#E5DEC9]">
              <th className="p-3">When</th><th className="p-3">SKU</th><th className="p-3">Change</th><th className="p-3">Reason</th><th className="p-3">By</th>
            </tr></thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-[#F4EFE6]">
                  <td className="p-3 text-xs">{new Date(l.at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="p-3 font-mono text-xs">{l.sku}</td>
                  <td className={`p-3 font-semibold ${l.change < 0 ? "text-[#8B1E21]" : "text-[#1A3C34]"}`}>{l.change > 0 ? `+${l.change}` : l.change}</td>
                  <td className="p-3 capitalize">{l.reason}</td>
                  <td className="p-3 text-xs text-[#9a938a]">{l.by}</td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#9a938a]">No inventory movements yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {adjust && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="inventory-adjust-modal">
          <form onSubmit={saveAdjust} className="bg-white p-6 w-full max-w-sm">
            <p className="font-serif text-xl mb-1">Adjust Stock</p>
            <p className="text-xs text-[#8C7A5E] mb-4">{adjust.product} · Size {adjust.size} · Current: {adjust.stock}</p>
            <label className="tq-label">Change (+ restock / − correction)</label>
            <input type="number" required value={form.change} onChange={(e) => setForm({ ...form, change: e.target.value })} data-testid="adjust-change" className="tq-input mb-3" placeholder="e.g. 10 or -2" />
            <label className="tq-label">Reason</label>
            <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="adjust-reason" className="tq-input bg-white mb-5">
              <option value="restock">Restock</option><option value="damaged">Damaged</option><option value="return">Return</option><option value="correction">Correction</option>
            </select>
            <div className="flex gap-2">
              <button type="submit" data-testid="adjust-save" className="tq-btn-dark flex-1">Save</button>
              <button type="button" onClick={() => setAdjust(null)} className="tq-btn-outline">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
